import { memo } from 'react'

import { useUser } from '@/client/context/user'
import type { ConversationAvatarsConversationFragment } from '@/client/services/hasura/generated/graphql'

import Avatar from '@/components/Worlds/UI/Avatar/Avatar'
import AvatarWithIcon from '@/components/Worlds/UI/Avatar/AvatarWithIcon'
import DiagonalAvatars from '@/components/Worlds/UI/Avatar/DiagonalAvatars'
import DiagonalAvatarsWithIcon from '@/components/Worlds/UI/Avatar/DiagonalAvatarsWithIcon'
import WorldIcon from '@/components/Worlds/UI/WorldIcon'

function ConversationAvatars({
  conversation,
  size,
}: {
  conversation: ConversationAvatarsConversationFragment
  size: 'sm' | 'md'
}): React.ReactElement {
  const currentUser = useUser()

  const { conversation_users, series, session } = conversation
  const users = conversation_users.map(user => user.user)

  const worldAndHosts =
    series?.world != null
      ? { world: series.world, hosts: series.series_hosts }
      : session?.world != null
        ? { world: session.world, hosts: session.session_hosts }
        : null

  if (worldAndHosts != null) {
    const { world, hosts } = worldAndHosts
    const nonHostUser = users.find(user => !hosts.some(host => host.user?.id === user.id)) ?? null

    const mainHost = hosts.find(host => host.role === 'main')

    if (mainHost == null || mainHost.user == null) {
      return <WorldIcon iconUrl={world.icon_url} primaryColor={world.primary_color} />
    }

    const secondaryHost = hosts.find(
      host => mainHost != null && host.role === 'main' && host.user.id !== mainHost.user.id
    )

    if (users.length === 1) {
      return (
        <AvatarWithIcon
          size={size}
          user={users[0]}
          iconEntity={{
            iconUrl: world.icon_url,
            primaryColor: world.primary_color,
          }}
        />
      )
    }

    return (
      <DiagonalAvatarsWithIcon
        userA={mainHost?.user ?? users[0]}
        userB={secondaryHost?.user ?? nonHostUser ?? users[0]}
        iconEntity={{
          iconUrl: world.icon_url,
          primaryColor: world.primary_color,
        }}
        size={size}
      />
    )
  }

  const otherUsers = users.filter(user => user.id !== currentUser.id)

  if (otherUsers.length > 1) {
    return <DiagonalAvatars userA={otherUsers[0]} userB={otherUsers[1]} size={size} />
  }

  return <Avatar size={size} user={otherUsers[0] ?? users[0]} />
}

export default memo(
  ConversationAvatars,
  (prevProps, nextProps) =>
    prevProps.conversation.id === nextProps.conversation.id &&
    prevProps.conversation.conversation_users.length ===
      nextProps.conversation.conversation_users.length
)
